import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Pagination,
    SelectChangeEvent,
    Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { TrackedSelect } from 'src/theme/StyledFields';
import NotificationFullCard from '../components/NotificationFullCard';
import ApiClient from '../config/ApiClient';
import { useAppContext } from '../context/AppContext';
import { ApiListResponse, Notifications } from '../object-actions/types/types';
import { formatNotificationTypeLabel } from '../utils/notificationUtils';

const CHANNEL_OPTIONS = [
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'all', label: 'All' },
] as const;

const NOTIFICATION_TYPE_OPTIONS = [
    'coach-content',
    'payment',
    'invoice',
    'assessment-submitted',
    'athlete-assessment-submitted',
    'athlete-post-assessment',
    'scheduling-email',
    'survey-invitation',
    'email-confirmation',
    'password-reset',
    'one-time-login',
    'unknown-account',
    'allauth-generic',
    'welcome-email',
    'assessment-reminder',
    'post-assessment-invite',
    'upgrade-invite',
    'coach-publish-reminder',
    'agent-generation-failed',
    'content-safety-alert',
    'assignment-invitation',
    'invitation-accepted',
    'intake-thank-you',
    'intake-ops-summary',
    'request-access-ops',
    'sms-verification',
    'session-feedback',
    'account-deletion-requested',
    'account-deletion-ops',
    'account-deletion-completed',
    'account-download-requested',
    'account-download-ops',
] as const;

const NotificationsScreen: React.FC = () => {
    const { organization, loading: contextLoading } = useAppContext();
    const [notifications, setNotifications] = useState<Notifications[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [channelFilter, setChannelFilter] = useState<string>('all');
    const [purposeFilter, setPurposeFilter] = useState<string>('all');
    const [seenFilter, setSeenFilter] = useState<string>('all');
    const [deliveryStatusFilter, setDeliveryStatusFilter] = useState<string>('all');
    const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
    const [dateTo, setDateTo] = useState<Dayjs | null>(null);
    const notificationsPerPage = 10;
    const previousOrganizationIdRef = useRef<number | undefined>(organization?.id);

    const fetchNotifications = async (pageNum: number = 1) => {
        if (!organization?.id) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                offset: ((pageNum - 1) * notificationsPerPage).toString(),
                limit: notificationsPerPage.toString(),
            });

            if (channelFilter !== 'all') {
                params.set('channel', channelFilter);
            }

            if (purposeFilter !== 'all') {
                params.set('notification_type', purposeFilter);
            }

            if (seenFilter !== 'all') {
                params.set('seen', seenFilter);
            }

            if (deliveryStatusFilter !== 'all') {
                params.set('delivery_status', deliveryStatusFilter);
            }

            if (dateFrom) {
                params.set('created_after', dateFrom.format('YYYY-MM-DD'));
            }

            if (dateTo) {
                params.set('created_before', dateTo.format('YYYY-MM-DD'));
            }

            const response = await ApiClient.get(`/api/notifications/?${params.toString()}`);

            if (response.success && response.data) {
                const data = response.data as ApiListResponse<'Notifications'>;
                setNotifications(data.results || []);
                setTotal(data.count || 0);
            } else {
                setError(response.error || 'Failed to load notifications');
            }
        } catch (err) {
            setError('Failed to load notifications');
            console.error('Error fetching notifications:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const organizationChanged = previousOrganizationIdRef.current !== organization?.id;
        previousOrganizationIdRef.current = organization?.id;

        if (!organization?.id) {
            setNotifications([]);
            setTotal(0);
            setLoading(contextLoading);
            return;
        }

        const pageToFetch = organizationChanged ? 1 : page;
        if (organizationChanged && page !== 1) {
            setPage(1);
        }

        fetchNotifications(pageToFetch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, channelFilter, purposeFilter, seenFilter, deliveryStatusFilter, dateFrom, dateTo, organization?.id, contextLoading]);


    const handlePageChange = (event: React.ChangeEvent<unknown>, pageNum: number) => {
        setPage(pageNum);
    };

    const handleChannelFilterChange = (event: SelectChangeEvent<string>) => {
        setChannelFilter(event.target.value);
        setPage(1);
    };

    const handlePurposeFilterChange = (event: SelectChangeEvent<string>) => {
        setPurposeFilter(event.target.value);
        setPage(1);
    };

    const handleSeenFilterChange = (event: SelectChangeEvent<string>) => {
        setSeenFilter(event.target.value);
        setPage(1); // Reset to first page when filter changes
    };

    const handleDeliveryStatusFilterChange = (event: SelectChangeEvent<string>) => {
        setDeliveryStatusFilter(event.target.value);
        setPage(1); // Reset to first page when filter changes
    };

    const handleDateFromChange = (value: Dayjs | null) => {
        setDateFrom(value);
        setPage(1);
    };

    const handleDateToChange = (value: Dayjs | null) => {
        setDateTo(value);
        setPage(1);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom  >
                Notification History
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Review notifications, delivery and usage for all users and purposes. For more detailed access logs contact your administrator.
            </Typography>

            {/* Filters */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Filters
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Channel</InputLabel>
                                <TrackedSelect
                                    value={channelFilter}
                                    label="Channel"
                                    onChange={handleChannelFilterChange}
                                >
                                    {CHANNEL_OPTIONS.map(({ value, label }) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </TrackedSelect>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Purpose</InputLabel>
                                <TrackedSelect
                                    value={purposeFilter}
                                    label="Purpose"
                                    onChange={handlePurposeFilterChange}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    {NOTIFICATION_TYPE_OPTIONS.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {formatNotificationTypeLabel(type)}
                                        </MenuItem>
                                    ))}
                                </TrackedSelect>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Seen Status</InputLabel>
                                <TrackedSelect
                                    value={seenFilter}
                                    label="Seen Status"
                                    onChange={handleSeenFilterChange}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="true">Seen</MenuItem>
                                    <MenuItem value="false">Unseen</MenuItem>
                                </TrackedSelect>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Delivery Status</InputLabel>
                                <TrackedSelect
                                    value={deliveryStatusFilter}
                                    label="Delivery Status"
                                    onChange={handleDeliveryStatusFilterChange}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="sent">Sent</MenuItem>
                                    <MenuItem value="delivered">Delivered</MenuItem>
                                    <MenuItem value="failed">Failed</MenuItem>
                                    <MenuItem value="bounced">Bounced</MenuItem>
                                </TrackedSelect>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <DatePicker
                                label="From Date"
                                value={dateFrom}
                                onChange={handleDateFromChange}
                                maxDate={dateTo ?? undefined}
                                slotProps={{
                                    textField: { size: 'small', fullWidth: true },
                                    field: { clearable: true },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <DatePicker
                                label="To Date"
                                value={dateTo}
                                onChange={handleDateToChange}
                                minDate={dateFrom ?? undefined}
                                slotProps={{
                                    textField: { size: 'small', fullWidth: true },
                                    field: { clearable: true },
                                }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Notifications List */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : notifications.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary" textAlign="center">
                            No notifications found matching your filters.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {notifications.length} of {total} notifications
                        </Typography>
                    </Box>

                    {notifications.map((notification) => (
                        <NotificationFullCard
                            key={notification.id}
                            notification={notification}
                        />
                    ))}

                    {total > notificationsPerPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={Math.ceil(total / notificationsPerPage)}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}
        </Container>
    );
};

export default NotificationsScreen;
